import { CacheProvider, ThemeProvider } from "@emotion/react";
import { KeyboardArrowDown } from "@mui/icons-material";
import { alpha, createTheme } from "@mui/material";
import { useSelector } from "react-redux";
import rtlPlugin from "stylis-plugin-rtl";
import { prefixer } from "stylis";
import createCache from "@emotion/cache";
import { arSD, enUS as gridEnUS } from "@mui/x-data-grid/locales";
import { useMemo, useState } from "react";
import { arSA, enUS } from "@mui/material/locale";
import "./jazi.css";
import "dayjs/locale/en";
import "dayjs/locale/ar";
import dayjs from "dayjs";

const DEFAULT_MAIN_COLOR = "#BB2436";

function normalizeMainColor(color) {
  if (typeof color === "string" && color.startsWith("#") && color.length >= 4) {
    return color;
  }
  return DEFAULT_MAIN_COLOR;
}

function AppTheme({ children }) {
  const direction = useSelector((state) => state.themeValues.direction);
  const cacheRtl = createCache({
    key: direction === "rtl" ? "mui-rtl" : "mui",
    stylisPlugins: direction === "rtl" ? [prefixer, rtlPlugin] : [],
  });
  const mainColor = normalizeMainColor(
    useSelector((state) => state.themeValues.mainColor)
  );
  const mode = useSelector((state) => state.themeValues.originalMode);
  const veryLighterColor = alpha(mainColor, 0.10);
  const lighterMainColor = alpha(mainColor, 0.15);
  const lightMainColor = alpha(mainColor, 0.8);
  const inputBorder = mode === "dark" ? "#232323" : "#DDDDDD";
  const inputBorderFocus = mainColor;
  const inputBackground = mode === "dark" ? "#191919cc" : "#fff";
  const JaziTheme = useMemo(() => {
    // Set dayjs locale based on direction (ltr or rtl)
    dayjs.locale(direction === "rtl" ? "ar" : "en"); // Use Arabic for rtl, English for ltr

    return createTheme(
      {
        direction: direction,
        typography: {
          fontFamily:
            direction === "ltr"
              ? `Inter, Poppins,"Nunito Sans", "Roboto", "Helvetica", "Arial", sans-serif`
              : `Inter, Tajawal, Poppins, "Roboto", "Helvetica", "Arial", sans-serif`,
          h4: {
            fontWeight: 500,
          },
        },
        palette: {
          mode: mode,
          text: {
            primary: mode === "dark" ? "#dfdddb" : "#202224",
            main: mode === "dark" ? "#dfdddb" : "#202224",
            dummy: mainColor,
          },
          primary: {
            main: mainColor,
            light: lightMainColor,
            lighter: lighterMainColor,
            veryLighter: veryLighterColor,
            containedLink: "#fff",
            contrastText: mode === "dark" ? "#121212" : "#ffffff",
            dimText: mode === "dark" ? "#968b8a" : "#697586",
            lighterText: createTheme().palette.augmentColor({
              color: { main: mode === "dark" ? "#d1d1d1" : "#2e2e2e" },
              name: "lighterText",
            }),
          },
          whiteblack: {
            main: mode === "dark" ? "#121212" : "#ffffff",
            prominent: mode === "dark" ? "#181818" : "#eeeeee",
            fixed: "#ffffff",
            fixedInactive: "#eeeeee",
            contrastText: mode === "dark" ? "#ededed" : "#000000",
          },
          lightButton: {
            main: lighterMainColor,
            contrastText: mode === "dark" ? "#dfdddb" : "#202224",
            dark: mode === "dark" ? "#8d8f00" : "#8280ff",
            border: mode === "dark" ? "#2b2b2b" : "#d5d5d5",
          },
          componentBackgrounds: {
            main: mode === "dark" ? "#121212" : "#ffffff",
            page: mode === "dark" ? "#181818" : "#fafafa",
            new: inputBackground,
            disabled: mode === "dark" ? "#282828" : "#f0f0f0",
            hover: mode === "dark" ? "#121212" : "#ffffff",
            contrastText: mode === "dark" ? "#ededed" : "#000000",
            border: inputBorder,
            borderSearch: mode === "dark" ? "#232323" : "#eaeaea",
            borderFocus: inputBorderFocus,
            placeholder: mode === "dark" ? "#444444" : "#bbbbbb",
            chip: mode === "dark" ? "" : "#00000014",
          },
          cancelText: {
            main: mode === "dark" ? "#909090" : "#7f7f7f",
            deleteIcon: "#EF3826",
            disabled: mode === "dark" ? "#555555" : "#aaaaaa",
            inactive: mode === "dark" ? "#888888" : "#aaaaaa",
            inactiveFixed: "#888888",
            contrastText: mode === "dark" ? "#ededed" : "#000000",
            border: mode === "dark" ? "#333333" : "#dddddd",
          },
        },
        shape: {
          borderRadius: 8,
        },
        components: {
          MuiTextField: {
            styleOverrides: {
              root: {
                backgroundColor: inputBackground,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: inputBorder,
                    // borderWidth: 2,
                  },
                  "&:hover fieldset": {
                    borderColor: inputBorderFocus,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: inputBorderFocus,
                  },
                },
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                marginLeft: 0,
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                backgroundColor: inputBackground,
                // borderColor:"red",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: inputBorder,
                  },
                  "&:hover fieldset": {
                    borderColor: inputBorderFocus,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: inputBorderFocus,
                  },
                  "&.MuiSelect-root": {
                    borderColor: "red",
                    borderWidth: 2,
                  },

                },
                ".MuiSvgIcon-root": {
                  color: mainColor,
                },
              },
            },
            defaultProps: {
              IconComponent: (props) => {
                return (
                  <KeyboardArrowDown
                    {...props}
                    sx={{ fontSize: 24 }}
                  ></KeyboardArrowDown>
                );
              },
            },
          },
          MuiDialogContent: {
            styleOverrides: { root: { paddingTop: `${8}px !important` } },
          },
          MuiAutocomplete: {
            defaultProps: {
              popupIcon: (
                <KeyboardArrowDown
                  color="primary"
                  sx={{ fontSize: 24 }}
                ></KeyboardArrowDown>
              ),
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px transparent inset',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                  transition: 'background-color 9999s ease-in-out 0s',
                },
              },
            },
          },
        },
      },
      direction === "rtl" ? arSA : enUS,
      direction === "rtl" ? arSD : gridEnUS
    );
  }, [direction, mode, mainColor]);
  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={JaziTheme}>{children}</ThemeProvider>
    </CacheProvider>
  );
}

function LogoAndText(props) {
  const mode = useSelector((state) => state.themeValues.originalMode);
  const [loaded, setLoaded] = useState(false);

  const handleImageLoad = () => {
    setLoaded(true);
  };
  //const fontSize = props.iFontSize ? props.iFontSize : 16;
  //const fontWeight = props.iFontWeight ? props.iFontWeight : 700;
  return (
    <>
      <img
        alt="logo"
        src={mode === "dark" ? "/fixafib_logo.svg" : "/fixafib_logo.svg"}
        onLoad={handleImageLoad}
        className={`logo ${loaded ? "reveal" : ""}`}
        style={{ width: 180 }}
      ></img>
      {/*<Typography
        color="primary"
        sx={{ fontSize: fontSize, fontWeight: fontWeight }}
      >
        Rewards Extend
      </Typography>*/}
    </>
  );
}

export { AppTheme, LogoAndText };
